import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaSearch, FaUser, FaHashtag, FaTimes } from 'react-icons/fa';
import { searchAPI, usersAPI, hashtagsAPI } from '../utils/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { useNavigate, Link } from 'react-router-dom';

const Search = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ posts: [], users: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const [usersRes, tagsRes] = await Promise.allSettled([
          searchAPI.users(query),
          hashtagsAPI.getTrending(5),
        ]);
        const userSuggs = usersRes.status === 'fulfilled' ? usersRes.value.data.slice(0, 4).map(u => ({ type: 'user', ...u })) : [];
        const tagSuggs = tagsRes.status === 'fulfilled' ? tagsRes.value.data.filter(t => t.tag?.toLowerCase().includes(query.toLowerCase())).slice(0, 3).map(t => ({ type: 'tag', tag: t.tag, count: t.count })) : [];
        setSuggestions([...userSuggs, ...tagSuggs]);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Autocomplete failed', err);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setShowSuggestions(false);
    setLoading(true);
    setHasSearched(true);
    try {
      const [postsRes, usersRes] = await Promise.all([
        searchAPI.posts(query),
        searchAPI.users(query)
      ]);
      setResults({ posts: postsRes.data, users: usersRes.data });
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (s) => {
    if (s.type === 'user') {
      navigate(`/profile/${s.id}`);
    } else {
      setQuery(s.tag);
      setShowSuggestions(false);
      handleSearch();
    }
  };

  const handleFollow = async (userId) => {
    try {
      await usersAPI.follow(userId);
      toast.success('User followed!');
    } catch {
      toast.error('Failed to follow user');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10 p-4 md:p-10 pb-24 md:pb-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-3xl md:text-4xl font-outfit font-bold text-gray-900 mb-6">Search</h1>

            <form onSubmit={handleSearch} className="mb-6 relative">
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="Search posts, users, or hashtags..."
                  className="w-full bg-gray-50 border border-gray-200 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] rounded-xl h-12 pl-11 pr-10 text-sm placeholder:text-gray-400 transition-all outline-none"
                  data-testid="search-input"
                />
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                {query && (
                  <button type="button" onClick={() => { setQuery(''); setSuggestions([]); setShowSuggestions(false); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <FaTimes size={12} />
                  </button>
                )}
              </div>

              {/* Autocomplete dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden" data-testid="search-suggestions">
                  {suggestions.map((s, i) => (
                    <button
                      key={s.id || `suggestion-${i}`}
                      onClick={() => handleSuggestionClick(s)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      data-testid={`suggestion-${i}`}
                    >
                      {s.type === 'user' ? (
                        <>
                          <img src={s.avatar_url || `https://ui-avatars.com/api/?name=${s.full_name}`} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{s.full_name}</p>
                            <p className="text-xs text-gray-500">@{s.username}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <FaHashtag className="text-gray-500 text-xs" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold">{s.tag}</p>
                            <p className="text-xs text-gray-500">{s.count} posts</p>
                          </div>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </form>

            {hasSearched && (
              <>
                <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-xl">
                  <button
                    onClick={() => setActiveTab('posts')}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      activeTab === 'posts' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Points ({results.posts.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('users')}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      activeTab === 'users' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    People ({results.users.length})
                  </button>
                </div>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A90E2]"></div>
                  </div>
                ) : (
                  <>
                    {activeTab === 'posts' && (
                      <div className="space-y-3">
                        {results.posts.length === 0 ? (
                          <p className="text-center text-gray-500 py-12 text-sm">No posts found</p>
                        ) : results.posts.map((post) => (
                          <div key={post.id} className="bg-white border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate('/feed')}>
                            <div className="flex items-center gap-3 mb-2">
                              <Link to={`/profile/${post.username}`} onClick={(e) => e.stopPropagation()}>
                                <img src={post.avatar_url || 'https://ui-avatars.com/api/?name=U'} alt="" className="w-9 h-9 rounded-full object-cover hover:ring-2 hover:ring-[#4A90E2]/40 transition-all" />
                              </Link>
                              <div>
                                <Link to={`/profile/${post.username}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
                                  <p className="text-sm font-bold">{post.full_name}</p>
                                </Link>
                                <p className="text-xs text-gray-500">@{post.username}</p>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700">{post.content}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeTab === 'users' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {results.users.length === 0 ? (
                          <p className="col-span-full text-center text-gray-500 py-12 text-sm">No users found</p>
                        ) : results.users.map((user) => (
                          <Link key={user.id} to={`/profile/${user.username}`} className="bg-white border border-gray-100 rounded-xl p-5 text-center hover:bg-gray-50 transition-colors block">
                            <img src={user.avatar_url || 'https://ui-avatars.com/api/?name=' + user.full_name} alt="" className="w-14 h-14 rounded-full object-cover mx-auto mb-3" />
                            <h3 className="font-bold text-sm">{user.full_name}</h3>
                            <p className="text-xs text-gray-500 mb-3">@{user.username}</p>
                            <button onClick={(e) => { e.preventDefault(); handleFollow(user.id); }} className="bg-gray-900 text-white text-xs font-semibold rounded-lg px-4 py-1.5 hover:bg-gray-700 transition-colors">
                              Follow
                            </button>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {!hasSearched && (
              <div className="text-center py-16">
                <FaSearch className="text-gray-200 text-4xl mx-auto mb-4" />
                <p className="text-gray-500 text-sm">Search for people, posts, or hashtags</p>
              </div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default Search;
